import { useState, useRef, useImperativeHandle, forwardRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Form } from '@/components/ui/form'
import {
  FormilySchemaRenderer,
  type IFormilyRendererRef,
} from '@/components/formily/renderer'
import { useUpdateEffect } from 'react-use'

const schemaToFormValue = (schema: FormilySchema) => {
  if (!schema || !schema.properties) return {}

  return Object.entries(schema.properties).reduce(
    (acc, [name, field]: [string, any]) => {
      const {
        type,
        title,
        required,
        description,
        'x-decorator-props': decoratorProps,
        'x-component': component,
        'x-component-props': componentProps,
        enum: enumList,
      } = field

      acc[name] = {
        name,
        type,
        title,
        required,
        description,
        tooltip: decoratorProps?.tooltip,
        placeholder: componentProps?.placeholder,
        options: component === 'Select' ? componentProps?.options : undefined,
        min: component === 'Slider' ? componentProps?.min : undefined,
        max: component === 'Slider' ? componentProps?.max : undefined,
        step: component === 'Slider' ? componentProps?.step : undefined,
        addonAfter:
          component === 'Input' ? componentProps?.addonAfter : undefined,
        enum: enumList?.map((item: any) => ({
          label: item.label,
          value: item.value,
        })),
      }

      return acc
    },
    {} as Record<string, any>,
  )
}

const formSchema = z.object({
  name: z.string().min(1, '字段名不能为空'),
  type: z.string(),
  title: z.string().min(1, '标题不能为空'),
  component: z.enum([
    'Input',
    'Select',
    'Checkbox',
    'Switch',
    'Radio',
    'Slider',
    'Textarea',
  ]),
  required: z.boolean(),
  tooltip: z.string().optional(),
  description: z.string().optional(),
  placeholder: z.string().optional(),
  options: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      }),
    )
    .optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().optional(),
  addonAfter: z.string().optional(),
  enum: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      }),
    )
    .optional(),
})

type FormValues = z.infer<typeof formSchema>

type FormilySchema = {
  type: string
  properties: Record<string, any>
}

interface DatabaseFormProps {
  schema: FormilySchema
  values?: Record<string, any>
}

export interface DatabaseFormRef {
  submit: () => Promise<Record<string, any> | undefined>
}

export const DatabaseForm = forwardRef<DatabaseFormRef, DatabaseFormProps>(
  ({ schema, values }, ref) => {
    const [formilySchema, setFormilySchema] = useState<FormilySchema>(
      schema || { type: 'object', properties: {} },
    )

    const form = useForm<FormValues>({
      resolver: zodResolver(formSchema),
      defaultValues: schemaToFormValue(schema),
    })

    const formilyRef = useRef<IFormilyRendererRef>(null)

    const submit = async () => {
      const formInstance = formilyRef.current?.form
      if (!formInstance) return

      try {
        const res = await formInstance.submit()
        return res as Record<string, any>
      } catch (err) {
        console.log('校验失败', err)
        throw err
      }
    }

    useImperativeHandle(ref, () => ({
      submit,
    }))

    useUpdateEffect(() => {
      schema && setFormilySchema(schema)
    }, [schema])

    return (
      <div className="flex h-screen px-4">
        <Form {...form}>
          <FormilySchemaRenderer
            ref={formilyRef}
            formilySchema={formilySchema}
            defaultValues={values}
          />
        </Form>
      </div>
    )
  },
)

DatabaseForm.displayName = 'DatabaseForm'
